<template>
  <span>
    <v-row dense>
      <v-col
        v-for="kpi in listaKpisGeral"
        :key="kpi.value"
        cols="12"
        :md="kpi.md"
      >
        <v-card
          class="my-0 fill-height"
          elevation="2"
          style="position: relative;"
        >
          <v-card-title class="d-flex align-center" style="min-width: 0;">
            <h5
              class="mb-0"
              :class="{ 'text-truncate': kpi.value === 'valor_financeiro' }"
              style="min-width: 0;"
            >
              {{ kpi.text }}
            </h5>
            <dashboard-isfp-psfp-faixas-info
              v-if="kpi.value === 'isfp_periodo'"
              title="Faixas de valores do ISFP Período"
            />
            <template v-if="kpi.value === 'valor_financeiro'">
              <v-spacer />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    class="d-inline-flex flex-shrink-0 ml-2"
                    v-on="on"
                  >
                    <v-btn
                      small
                      outlined
                      color="primary"
                      elevation="1"
                      class="font-weight-bold text-none"
                      aria-label="Parcela B"
                      :loading="parcelaBLoading"
                      :disabled="parcelaBDisabled"
                      @click.stop="$emit('abrir-parcela-b')"
                    >
                      <v-icon left small>mdi-currency-brl</v-icon>
                      Parcela B
                    </v-btn>
                  </span>
                </template>
                <span>Informar ou atualizar Parcela B</span>
              </v-tooltip>
            </template>
          </v-card-title>

          <div
            v-if="loading"
            class="d-flex align-center justify-center"
            style="height: 90px;"
          >
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>

          <v-card-text v-else>
            <div class="display-3" :style="getKpiValueStyle(kpi.value)">
              {{ getKpiDisplayValue(kpi) }}
              <span v-if="getKpiSuffix(kpi)">{{ getKpiSuffix(kpi) }}</span>
            </div>

            <div
              v-if="getKpiSecondaryText(kpi)"
              class="text-body-2 font-weight-medium mt-1"
              :style="getKpiValueStyle(kpi.value)"
            >
              {{ getKpiSecondaryText(kpi) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </span>
</template>

<script>
import DashboardIsfpPsfpFaixasInfo from "@/components/dashboardIsfpPsfp/DashboardIsfpPsfpFaixasInfo.vue";
import { getCorIsfpPsfpPorValor } from "@/utils/isfpPsfpFaixas";

export default {
  name: "DashboardIsfpPsfpKpisGeral",
  components: {
    DashboardIsfpPsfpFaixasInfo,
  },
  props: {
    kpis: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    parcelaBLoading: { type: Boolean, default: false },
    parcelaBDisabled: { type: Boolean, default: false },
  },
  data: () => ({
    listaKpisLinha1: [
      { value: "isfp_periodo", text: "ISFP Período", suffix: "%", md: 2 },
      { value: "sa_total", text: "SA Total", suffix: "", md: 2 },
      { value: "sfp_total", text: "SFP Total", suffix: "", md: 2 },
    ],
    listaKpisLinha2: [
      { value: "qisfp", text: "QISFP", suffix: "%", md: 2 },
      {
        value: "valor_financeiro",
        text: "Valor Financeiro",
        suffix: "",
        md: 4,
      },
    ],
  }),
  computed: {
    listaKpisGeral() {
      return [...this.listaKpisLinha1, ...this.listaKpisLinha2];
    },
  },
  methods: {
    getKpiRawValue(kpi) {
      if (kpi.value === "qisfp") {
        return this.kpis?.qisfp?.valor ?? null;
      }

      return this.kpis?.[kpi.value] ?? null;
    },

    formatarPercentualComSinal(valor) {
      if (valor === null || valor === undefined || isNaN(valor)) return "-";

      const numero = Number(valor);
      const sinal = numero > 0 ? "+" : numero < 0 ? "-" : "";
      const absoluto = Math.abs(numero)
        .toFixed(2)
        .replace(".", ",");

      return `${sinal}${absoluto}`;
    },

    formatarMoeda(valor) {
      if (valor === null || valor === undefined || valor === "") return "";

      return Number(valor).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getKpiDisplayValue(kpi) {
      const valor = this.getKpiRawValue(kpi);

      if (kpi.value === "qisfp") {
        return this.formatarPercentualComSinal(valor);
      }

      if (kpi.value === "valor_financeiro") {
        return valor === null || valor === undefined
          ? ""
          : `R$ ${this.formatarMoeda(valor)}`;
      }

      if (valor === null || valor === undefined || valor === "") {
        return "-";
      }

      return this.$options.filters.parseNumberToFloatBrIgnoringNaN(valor);
    },

    getKpiSecondaryText(kpi) {
      if (kpi.value === "qisfp") {
        return this.kpis?.qisfp?.faixa ?? "-";
      }
      if (kpi.value === "valor_financeiro") {
        const valor = this.getKpiRawValue(kpi);
        if (valor === null || valor === undefined || valor === "") {
          return "Preencha o valor da Parcela B";
        }
      }

      return "";
    },

    getKpiSuffix(kpi) {
      if (kpi.value === "valor_financeiro") {
        return "";
      }

      const valor = this.getKpiRawValue(kpi);

      if (valor === null || valor === undefined || valor === "") {
        return "";
      }

      return kpi.suffix;
    },

    getKpiValueStyle(kpiKey) {
      if (kpiKey === "isfp_periodo") {
        return {
          color: getCorIsfpPsfpPorValor(this.kpis[kpiKey]),
        };
      }

      if (kpiKey === "qisfp") {
        return {
          color: getCorIsfpPsfpPorValor(
            this.kpis?.qisfp?.isfp_referencia ?? 0,
          ),
        };
      }

      return {};
    },
  },
};
</script>
